import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  TrendingDown,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  Globe,
  Zap,
  Calendar,
  FileText,
  Search,
  Download,
  CheckCircle,
  Target,
  DollarSign,
  Eye,
  Clock,
  RefreshCw,
  Briefcase,
  Users,
  Award,
  Plus,
  Edit2,
  Trash2,
} from 'lucide-react'
import { useCompetitorStore } from '../store/competitorStore'

export default function CompetitorIntelligencePage() {
  const navigate = useNavigate()
  const competitors = useCompetitorStore((state) => state.competitors)
  const addCompetitor = useCompetitorStore((state) => state.addCompetitor)
  const updateCompetitor = useCompetitorStore((state) => state.updateCompetitor)
  const deleteCompetitor = useCompetitorStore((state) => state.deleteCompetitor)

  const [activeTab, setActiveTab] = useState('overview')
  const [selectedCompetitor, setSelectedCompetitor] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    website: '',
    headquarters: '',
    foundedYear: new Date().getFullYear(),
    employeeCount: 0,
    marketPosition: '',
    marketShare: 0,
    strengths: '',
    weaknesses: '',
    opportunities: '',
    threats: '',
    keyProducts: '',
    pricingModel: '',
    targetMarket: '',
    annualRevenue: '',
    fundingStatus: '',
    primaryContact: '',
    title: '',
    email: '',
    phone: '',
    riskLevel: 'MEDIUM',
    notes: '',
  })

  const highRiskCount = useCompetitorStore((state) => state.getHighRiskCompetitors())
  const averageMarketShare = useCompetitorStore((state) => state.getAverageMarketShare())

  const stats = {
    competitorsMonitored: competitors.length,
    priceChanges: Math.floor(competitors.length * 0.67),
    newsArticles: Math.floor(competitors.length * 2.8),
    strategicInsights: Math.floor(competitors.length * 13),
    lastUpdate: 'Just now',
    dataPoints: competitors.length * 237,
  }

  const filteredCompetitors = competitors.filter(comp =>
    comp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    comp.industry.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleAddClick = () => {
    setFormData({
      name: '',
      industry: '',
      website: '',
      headquarters: '',
      foundedYear: new Date().getFullYear(),
      employeeCount: 0,
      marketPosition: '',
      marketShare: 0,
      strengths: '',
      weaknesses: '',
      opportunities: '',
      threats: '',
      keyProducts: '',
      pricingModel: '',
      targetMarket: '',
      annualRevenue: '',
      fundingStatus: '',
      primaryContact: '',
      title: '',
      email: '',
      phone: '',
      riskLevel: 'MEDIUM',
      notes: '',
    })
    setSelectedCompetitor(null)
    setShowAddModal(true)
  }

  const handleEditClick = (competitor) => {
    setSelectedCompetitor(competitor)
    setFormData({
      name: competitor.name,
      industry: competitor.industry,
      website: competitor.website,
      headquarters: competitor.headquarters,
      foundedYear: competitor.foundedYear,
      employeeCount: competitor.employeeCount,
      marketPosition: competitor.marketPosition,
      marketShare: competitor.marketShare,
      strengths: competitor.strengths.join(', '),
      weaknesses: competitor.weaknesses.join(', '),
      opportunities: competitor.opportunities.join(', '),
      threats: competitor.threats.join(', '),
      keyProducts: competitor.keyProducts.join(', '),
      pricingModel: competitor.pricingModel,
      targetMarket: competitor.targetMarket,
      annualRevenue: competitor.financialData.annualRevenue,
      fundingStatus: competitor.financialData.fundingStatus,
      primaryContact: competitor.contactInfo.primaryContact,
      title: competitor.contactInfo.title,
      email: competitor.contactInfo.email,
      phone: competitor.contactInfo.phone,
      riskLevel: competitor.riskLevel,
      notes: competitor.notes,
    })
    setShowEditModal(true)
  }

  const handleSave = () => {
    if (!formData.name || !formData.industry) {
      alert('Please fill in required fields: Name and Industry')
      return
    }

    const competitorData = {
      name: formData.name,
      industry: formData.industry,
      website: formData.website,
      headquarters: formData.headquarters,
      foundedYear: parseInt(formData.foundedYear),
      employeeCount: parseInt(formData.employeeCount),
      marketPosition: formData.marketPosition,
      marketShare: parseFloat(formData.marketShare),
      strengths: formData.strengths.split(',').map(s => s.trim()).filter(s => s),
      weaknesses: formData.weaknesses.split(',').map(s => s.trim()).filter(s => s),
      opportunities: formData.opportunities.split(',').map(s => s.trim()).filter(s => s),
      threats: formData.threats.split(',').map(s => s.trim()).filter(s => s),
      keyProducts: formData.keyProducts.split(',').map(s => s.trim()).filter(s => s),
      pricingModel: formData.pricingModel,
      targetMarket: formData.targetMarket,
      financialData: {
        annualRevenue: formData.annualRevenue,
        fundingStatus: formData.fundingStatus,
        growthRate: '0%',
      },
      contactInfo: {
        primaryContact: formData.primaryContact,
        title: formData.title,
        email: formData.email,
        phone: formData.phone,
      },
      socialMedia: {
        linkedin: '',
        twitter: '',
        facebook: '',
      },
      riskLevel: formData.riskLevel,
      notes: formData.notes,
    }

    if (selectedCompetitor) {
      updateCompetitor(selectedCompetitor.id, competitorData)
      setShowEditModal(false)
    } else {
      addCompetitor(competitorData)
      setShowAddModal(false)
    }
    setSelectedCompetitor(null)
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this competitor?')) {
      deleteCompetitor(id)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value })
  }

  const getRiskColor = (riskLevel) => {
    switch(riskLevel) {
      case 'HIGH': return 'bg-red-600/20 text-red-300'
      case 'MEDIUM': return 'bg-yellow-600/20 text-yellow-300'
      case 'LOW': return 'bg-green-600/20 text-green-300'
      default: return 'bg-slate-600/20 text-slate-300'
    }
  }

  const recentPriceChanges = [
    {
      competitor: 'TechCorp Solutions',
      plan: 'Professional',
      oldPrice: '$599',
      newPrice: '$649',
      changePercent: '+8.3%',
      date: '2024-04-24',
      impact: 'High',
    },
    {
      competitor: 'Digital Innovations Inc',
      plan: 'Enterprise',
      oldPrice: '$499',
      newPrice: '$479',
      changePercent: '-4.0%',
      date: '2024-04-22',
      impact: 'Medium',
    },
    {
      competitor: 'CloudPro Services',
      plan: 'Starter',
      oldPrice: '$249',
      newPrice: '$299',
      changePercent: '+20.1%',
      date: '2024-04-20',
      impact: 'High',
    },
    {
      competitor: 'InnovateTech Corp',
      plan: 'Professional',
      oldPrice: '$899',
      newPrice: '$899',
      changePercent: '0%',
      date: '2024-04-18',
      impact: 'Low',
    },
  ]

  const newsInsights = [
    {
      id: 1,
      source: 'TechCrunch',
      headline: 'TechCorp Solutions announces AI-powered features',
      date: '2024-04-25',
      sentiment: 'Positive',
      impact: 'High',
      relevance: 95,
      category: 'Product Launch',
    },
    {
      id: 2,
      source: 'Venture Beat',
      headline: 'Digital Innovations secures $50M Series B funding',
      date: '2024-04-24',
      sentiment: 'Positive',
      impact: 'High',
      relevance: 88,
      category: 'Funding',
    },
    {
      id: 3,
      source: 'Business Wire',
      headline: 'CloudPro Services expands to Asian markets',
      date: '2024-04-23',
      sentiment: 'Positive',
      impact: 'Medium',
      relevance: 76,
      category: 'Expansion',
    },
    {
      id: 4,
      source: 'Forbes',
      headline: 'InnovateTech Corp faces security concerns in latest audit',
      date: '2024-04-22',
      sentiment: 'Negative',
      impact: 'High',
      relevance: 92,
      category: 'Risk',
    },
    {
      id: 5,
      source: 'ZDNet',
      headline: 'Industry analysis: Cloud market consolidation trends',
      date: '2024-04-21',
      sentiment: 'Neutral',
      impact: 'Medium',
      relevance: 83,
      category: 'Market Analysis',
    },
  ]

  const strategicInsights = [
    {
      title: 'Price War Alert',
      description: 'TechCorp Solutions increased prices by 8.3%, positioning themselves as premium tier',
      priority: 'High',
      action: 'Review your pricing strategy against competitor positioning',
      date: '2024-04-25',
    },
    {
      title: 'Feature Gap Identified',
      description: 'InnovateTech Corp now leads with 31 core features vs our 24. AI-powered features are becoming standard',
      priority: 'High',
      action: 'Prioritize feature development roadmap to close the gap',
      date: '2024-04-24',
    },
    {
      title: 'Market Expansion Opportunity',
      description: 'CloudPro expanding to Asia-Pacific. Emerging market opportunity detected',
      priority: 'Medium',
      action: 'Develop localization strategy for emerging markets',
      date: '2024-04-23',
    },
    {
      title: 'Competitor Vulnerability',
      description: 'InnovateTech facing security audit concerns. Market trust metric declining',
      priority: 'Medium',
      action: 'Emphasize your security certifications in marketing messaging',
      date: '2024-04-22',
    },
  ]

  const capabilities = [
    { name: 'Website Monitoring', status: 'Active', frequency: 'Every 6 hours' },
    { name: 'Pricing Intelligence', status: 'Active', frequency: 'Real-time' },
    { name: 'News Aggregation', status: 'Active', frequency: 'Every 2 hours' },
    { name: 'Feature Tracking', status: 'Active', frequency: 'Daily' },
    { name: 'Social Listening', status: 'Active', frequency: 'Real-time' },
    { name: 'Report Generation', status: 'Active', frequency: 'Weekly' },
  ]

  const features = [
    {
      icon: Globe,
      title: 'Website Monitoring',
      description: 'Automated tracking of competitor websites, product pages, and pricing updates',
    },
    {
      icon: DollarSign,
      title: 'Pricing Intelligence',
      description: 'Real-time price tracking across all plans with historical comparison and trend analysis',
    },
    {
      icon: Briefcase,
      title: 'Feature Comparison',
      description: 'Comprehensive feature matrix tracking and capability gap analysis',
    },
    {
      icon: TrendingUp,
      title: 'Market Intelligence',
      description: 'Market share tracking, growth metrics, and competitive positioning analysis',
    },
    {
      icon: FileText,
      title: 'News Intelligence',
      description: 'AI-powered news aggregation and sentiment analysis from 500+ sources',
    },
    {
      icon: Target,
      title: 'Strategic Reports',
      description: 'Weekly AI-generated strategic reports with actionable insights and recommendations',
    },
    {
      icon: Users,
      title: 'Social Listening',
      description: 'Monitor social media mentions, reviews, and customer sentiment about competitors',
    },
    {
      icon: Award,
      title: 'Risk Assessment',
      description: 'Identify competitive threats and market opportunities with predictive analytics',
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => navigate(-1)} className="text-blue-400 hover:text-blue-300 text-2xl">←</button>
              <h1 className="text-4xl font-black text-white">Competitor Intelligence Bot</h1>
            </div>
            <p className="text-slate-400">AI-Driven Market Intelligence & Strategic Analysis</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleAddClick}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-semibold transition-all flex items-center gap-2"
            >
              <Plus size={20} />
              Add Competitor
            </button>
            <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all">
              <Download size={20} className="inline mr-2" />
              Generate Report
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Competitors Tracked</p>
                <p className="text-2xl font-black text-white">{stats.competitorsMonitored}</p>
              </div>
              <Briefcase size={32} className="text-blue-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">High Risk Threats</p>
                <p className="text-2xl font-black text-red-400">{highRiskCount}</p>
              </div>
              <AlertTriangle size={32} className="text-red-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg Market Share</p>
                <p className="text-2xl font-black text-green-400">{averageMarketShare}%</p>
              </div>
              <TrendingUp size={32} className="text-green-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Strategic Insights</p>
                <p className="text-2xl font-black text-white">{stats.strategicInsights}</p>
              </div>
              <Zap size={32} className="text-orange-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Data Points</p>
                <p className="text-2xl font-black text-white">{stats.dataPoints}</p>
              </div>
              <BarChart3 size={32} className="text-green-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Last Update</p>
                <p className="text-xl font-black text-white">{stats.lastUpdate}</p>
              </div>
              <RefreshCw size={32} className="text-indigo-400 opacity-50" />
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-blue-400" />
          <input
            type="text"
            placeholder="Search competitors by name or industry..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-800 border border-blue-500/20 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-blue-500/20 pb-4 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'competitors', label: 'Competitors', icon: Briefcase },
          { id: 'pricing', label: 'Price Intelligence', icon: DollarSign },
          { id: 'news', label: 'News & Insights', icon: FileText },
          { id: 'strategic', label: 'Strategic Reports', icon: Target },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-600/40 to-cyan-600/40 text-blue-200 border border-blue-500/50'
                : 'text-slate-400 hover:text-blue-300'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Features Grid */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">Intelligence Capabilities</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-6 rounded-lg border border-blue-500/20 hover:border-blue-500/40 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-600/20 rounded-lg">
                      <feature.icon size={24} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">{feature.title}</h3>
                      <p className="text-sm text-slate-400">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Capabilities */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">Monitoring Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {capabilities.map((cap, idx) => (
                <div key={idx} className="bg-slate-800/50 p-4 rounded-lg border border-green-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-white">{cap.name}</h3>
                    <span className="text-xs px-2 py-1 bg-green-600/20 text-green-300 rounded-full">
                      {cap.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">{cap.frequency}</span>
                    <CheckCircle size={16} className="text-green-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Competitors Tab */}
      {activeTab === 'competitors' && (
        <div>
          {filteredCompetitors.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredCompetitors.map(competitor => (
                <div key={competitor.id} className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20 hover:border-blue-400/40 transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white">{competitor.name}</h3>
                      <p className="text-sm text-slate-400">{competitor.industry}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${getRiskColor(competitor.riskLevel)}`}>
                      {competitor.riskLevel} RISK
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-400">Market Share</p>
                        <p className="font-bold text-green-400">{competitor.marketShare}%</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Employees</p>
                        <p className="font-bold text-white">{competitor.employeeCount}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Founded</p>
                        <p className="font-bold text-yellow-400">{competitor.foundedYear}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Position</p>
                        <p className="font-bold text-purple-400 text-xs">{competitor.marketPosition}</p>
                      </div>
                    </div>
                    {competitor.keyProducts && competitor.keyProducts.length > 0 && (
                      <div>
                        <p className="text-slate-400 text-sm mb-2">Key Products</p>
                        <div className="flex flex-wrap gap-2">
                          {competitor.keyProducts.slice(0, 3).map((product, idx) => (
                            <span key={idx} className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded">
                              {product}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="pt-3 border-t border-slate-700 flex gap-2">
                      <button
                        onClick={() => handleEditClick(competitor)}
                        className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2 rounded-lg transition-all flex items-center justify-center gap-2"
                      >
                        <Edit2 size={16} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(competitor.id)}
                        className="flex-1 bg-red-600/20 hover:bg-red-600/40 text-red-300 font-semibold py-2 rounded-lg transition-all flex items-center justify-center gap-2"
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Briefcase size={48} className="text-slate-400 mx-auto mb-4" />
              <p className="text-slate-400 text-lg">No competitors found. Add one to get started!</p>
            </div>
          )}
        </div>
      )}

      {/* Pricing Tab */}
      {activeTab === 'pricing' && (
        <div className="space-y-4">
          {recentPriceChanges.map((change, idx) => (
            <div key={idx} className="bg-slate-800/50 p-4 rounded-lg border border-blue-500/20">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-bold text-white">{change.competitor}</h3>
                  <p className="text-sm text-slate-400">{change.plan} Plan</p>
                </div>
                <div className="text-right">
                  <div className="flex gap-3 items-center">
                    <div className="text-right">
                      <p className="text-sm text-slate-400 line-through">{change.oldPrice}</p>
                      <p className="font-bold text-white">{change.newPrice}</p>
                    </div>
                    <div className={`text-lg font-bold px-3 py-1 rounded ${
                      change.changePercent.startsWith('+')
                        ? 'bg-red-600/20 text-red-300'
                        : 'bg-green-600/20 text-green-300'
                    }`}>
                      {change.changePercent}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <span className="text-xs text-slate-400">{change.date}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      change.impact === 'High'
                        ? 'bg-red-600/20 text-red-300'
                        : change.impact === 'Medium'
                        ? 'bg-yellow-600/20 text-yellow-300'
                        : 'bg-green-600/20 text-green-300'
                    }`}>
                      {change.impact} Impact
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* News Tab */}
      {activeTab === 'news' && (
        <div className="space-y-4">
          {newsInsights.map(news => (
            <div key={news.id} className="bg-slate-800/50 p-4 rounded-lg border border-blue-500/20">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-bold text-white mb-1">{news.headline}</h3>
                  <div className="flex gap-2 text-sm">
                    <span className="text-slate-400">{news.source}</span>
                    <span className="text-slate-500">•</span>
                    <span className="text-slate-400">{news.date}</span>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ml-2 ${
                  news.sentiment === 'Positive'
                    ? 'bg-green-600/20 text-green-300'
                    : news.sentiment === 'Negative'
                    ? 'bg-red-600/20 text-red-300'
                    : 'bg-slate-600/20 text-slate-300'
                }`}>
                  {news.sentiment}
                </span>
              </div>
              <div className="flex gap-2 mt-3">
                <span className="text-xs bg-blue-600/20 text-blue-300 px-2 py-1 rounded">
                  {news.category}
                </span>
                <span className="text-xs text-slate-400">Relevance: {news.relevance}%</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Strategic Reports Tab */}
      {activeTab === 'strategic' && (
        <div className="space-y-4">
          {strategicInsights.map((insight, idx) => (
            <div key={idx} className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold text-white">{insight.title}</h3>
                  <p className="text-slate-400 text-sm mt-2">{insight.description}</p>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${
                  insight.priority === 'High'
                    ? 'bg-red-600/20 text-red-300'
                    : 'bg-yellow-600/20 text-yellow-300'
                }`}>
                  {insight.priority} Priority
                </span>
              </div>
              <div className="pt-4 border-t border-slate-700">
                <p className="text-sm font-semibold text-white mb-2">Recommended Action:</p>
                <p className="text-sm text-slate-300">{insight.action}</p>
                <p className="text-xs text-slate-500 mt-3">{insight.date}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between bg-gradient-to-r from-slate-700 to-slate-800 p-6 border-b border-blue-500/20 sticky top-0">
              <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                <Target className="w-6 h-6 text-amber-400" />
                <span>{showEditModal ? 'Edit Competitor' : 'Add New Competitor'}</span>
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setShowEditModal(false)
                  setSelectedCompetitor(null)
                }}
                className="text-slate-400 hover:text-white text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Company Name *"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="Industry *"
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="Website"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="Headquarters"
                  value={formData.headquarters}
                  onChange={(e) => handleInputChange('headquarters', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Founded Year"
                  value={formData.foundedYear}
                  onChange={(e) => handleInputChange('foundedYear', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Employee Count"
                  value={formData.employeeCount}
                  onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="Market Position"
                  value={formData.marketPosition}
                  onChange={(e) => handleInputChange('marketPosition', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Market Share %"
                  value={formData.marketShare}
                  onChange={(e) => handleInputChange('marketShare', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
              </div>

              <div>
                <label className="text-blue-300 text-sm font-semibold mb-2 block">Risk Level</label>
                <select
                  value={formData.riskLevel}
                  onChange={(e) => handleInputChange('riskLevel', e.target.value)}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-400/50"
                >
                  <option value="LOW">Low Risk</option>
                  <option value="MEDIUM">Medium Risk</option>
                  <option value="HIGH">High Risk</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <textarea
                  placeholder="Strengths (comma-separated)"
                  value={formData.strengths}
                  onChange={(e) => handleInputChange('strengths', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50 min-h-[100px]"
                />
                <textarea
                  placeholder="Weaknesses (comma-separated)"
                  value={formData.weaknesses}
                  onChange={(e) => handleInputChange('weaknesses', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50 min-h-[100px]"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <textarea
                  placeholder="Key Products (comma-separated)"
                  value={formData.keyProducts}
                  onChange={(e) => handleInputChange('keyProducts', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50 min-h-[80px]"
                />
                <textarea
                  placeholder="Pricing Model"
                  value={formData.pricingModel}
                  onChange={(e) => handleInputChange('pricingModel', e.target.value)}
                  className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50 min-h-[80px]"
                />
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <h3 className="text-blue-300 font-semibold mb-3">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Primary Contact Name"
                    value={formData.primaryContact}
                    onChange={(e) => handleInputChange('primaryContact', e.target.value)}
                    className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                  />
                  <input
                    type="text"
                    placeholder="Title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                  />
                </div>
              </div>

              <textarea
                placeholder="Notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50 min-h-[100px]"
              />
            </div>

            {/* Modal Footer */}
            <div className="flex space-x-3 p-6 bg-slate-700/50 border-t border-blue-500/20 sticky bottom-0">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setShowEditModal(false)
                  setSelectedCompetitor(null)
                }}
                className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-semibold py-2 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2 rounded-lg transition-all"
              >
                {showEditModal ? 'Update' : 'Add'} Competitor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
